/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { serverResponseErrorsCodes } from '../../utils/consts';
import { sendSentryError } from '../../utils/sentry';
import { isServerErrorOfType } from '../../utils/server-helpers';
import { useLogout, useSignIn } from '../../hooks';
import { useGoogleSignInMutation } from '../../apollo';
import { useAlert } from '../../providers';
import { AuthLogo } from '../../components/AuthLogo';
import { AuthPageChangeBlock } from '../../components/AuthPageChangeBlock';
import { AuthSplitBlock } from '../../components/AuthSplitBlock';
import { GoogleSigninButton } from '../../components/buttons';
import { LoginForm } from '../../components/forms';
import { AuthPageTitle } from '../../components/texts';
import { AuthLayout } from '../../layouts';
import { SignInAndUpContentWrapper } from '../../styled/SignInAndUpContentWrapper.ts';

const LoginPage = () => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();

  const rememberFlagRef = useRef(false);

  const signInHandler = useSignIn();

  const [signIn] = useGoogleSignInMutation({
    fetchPolicy: 'no-cache',
  });

  useLogout();

  const onGoogleSignIn = async (token: string) => {
    try {
      const { data } = await signIn({
        variables: {
          token,
        },
      });

      if (!data?.googleSignIn) {
        throw new Error('No google sign in data');
      }

      signInHandler({
        data: data.googleSignIn,
        config: {
          socialProvider: true,
          rememberFlag: rememberFlagRef.current,
        },
      });
    } catch (error) {
      const googleUserNotFoundError = isServerErrorOfType({
        error,
        type: serverResponseErrorsCodes.GOOGLE_USER_NOT_FOUND,
      });

      if (googleUserNotFoundError) {
        setAlert({
          type: 'warning',
          message: t('LOGIN_PAGE__googleAccountNotExistError'),
        });
        return;
      }

      setAlert({
        type: 'error',
        message: t('GOOGLE_AUTH_BUTTON__error'),
      });

      sendSentryError(error);
    }
  };

  return (
    <AuthLayout>
      <SignInAndUpContentWrapper>
        <AuthLogo />
        <AuthPageTitle>{t('LOGIN_PAGE__title')}</AuthPageTitle>
        <GoogleSigninButton onSuccess={onGoogleSignIn} />
        <AuthSplitBlock />
        <LoginForm rememberFlagRef={rememberFlagRef} />
        <AuthPageChangeBlock />
      </SignInAndUpContentWrapper>
    </AuthLayout>
  );
};

export { LoginPage };
